import { ReactElement } from 'react';
import { renderToStaticMarkup } from 'react-dom/server';
import { hexOrRGBStringToPixel, rgbStringToPixel } from './colours';
import { Pantone, getClosestPantone, pantoneToHex } from './pantone';

export function svgToURL(component: ReactElement) {
	const svgString = renderToStaticMarkup(component);
	if (typeof window === 'undefined') {
		//Use node's Buffer
		const { Buffer } = require('buffer');
		return 'data:image/svg+xml;base64,' + Buffer.from(svgString).toString('base64');
	}

	//Use window btoa to convert to base64
	return 'data:image/svg+xml;base64,' + window.btoa(svgString);
}

export function svgToBuffer(component: ReactElement) {
	const svgString = renderToStaticMarkup(component);
	let buffer: Buffer;

	if (typeof window === 'undefined') {
		//Use node's Buffer
		const { Buffer } = require('buffer');
		buffer = (Buffer as BufferConstructor).from(svgString);
	} else {
		buffer = Buffer.from(window.btoa(svgString));
	}

	return buffer;
}

export function extractSvgColors(svgString: string): number[][] {
	if (typeof document === 'undefined') return [];

	// Create a temporary container for the SVG
	const tempContainer = document.createElement('div');
	tempContainer.style.visibility = 'hidden'; // Hide the container
	tempContainer.innerHTML = svgString;
	document.body.appendChild(tempContainer);

	const colours: number[][] = [];

	const extractColors = (node: Element) => {
		const computedStyle = window.getComputedStyle(node);
		const fillColor: string = computedStyle.fill;
		const strokeColor: string = computedStyle.stroke;

		if (fillColor && fillColor !== 'none' && fillColor !== '') {
			const colour = hexOrRGBStringToPixel(fillColor);

			if (!colours.some(c => c[0] === colour[0] && c[1] === colour[1] && c[2] === colour[2])) {
				colours.push(colour);
			}
		}
		if (strokeColor && strokeColor !== 'none' && strokeColor !== '') {
			const colour = hexOrRGBStringToPixel(strokeColor);

			if (!colours.some(c => c[0] === colour[0] && c[1] === colour[1] && c[2] === colour[2])) {
				colours.push(colour);
			}
		}
	};

	const traverseNodes = (node: Node) => {
		if (node.nodeType === Node.ELEMENT_NODE) {
			extractColors(node as Element);
		}
		node.childNodes.forEach(child => traverseNodes(child));
	};

	// Start the traversal from the temporary container
	traverseNodes(tempContainer);

	// Clean up: remove the temporary SVG container
	document.body.removeChild(tempContainer);

	return Array.from(colours);
}

export function replaceSVGColours(svgString: string, replacements: Record<string, Pantone> = {}, colourList?: Pantone[]) {
	if (typeof document === 'undefined') return svgString;

	// Create a temporary container for the SVG
	const tempContainer = document.createElement('div');
	tempContainer.style.visibility = 'hidden'; // Hide the container
	tempContainer.innerHTML = svgString;
	document.body.appendChild(tempContainer);

	const replaceColors = (node: HTMLElement) => {
		const computedStyle = window.getComputedStyle(node);
		const fillColor: string = computedStyle.fill;
		const strokeColor: string = computedStyle.stroke;

		if (fillColor && fillColor !== 'none' && fillColor !== '') {
			const p = getClosestPantone(rgbStringToPixel(fillColor), colourList);
			const replacement = replacements[p.name];
			if (replacement) {
				node.style.fill = pantoneToHex(replacement);
			} else if (colourList) {
				node.style.fill = pantoneToHex(p);
			}
		}
		if (strokeColor && strokeColor !== 'none' && strokeColor !== '') {
			const p = getClosestPantone(rgbStringToPixel(strokeColor), colourList);
			const replacement = replacements[p.name];
			if (replacement) {
				node.style.stroke = pantoneToHex(replacement);
			} else if (colourList) {
				node.style.stroke = pantoneToHex(p);
			}
		}
	};

	const traverseNodes = (node: Node) => {
		if (node.nodeType === Node.ELEMENT_NODE) {
			replaceColors(node as HTMLElement);
		}
		node.childNodes.forEach(child => traverseNodes(child));
	};

	// Start the traversal from the temporary container
	traverseNodes(tempContainer);

	// Clean up: remove the temporary SVG container
	const newSvgString = tempContainer.innerHTML;
	document.body.removeChild(tempContainer);

	return newSvgString;
}

export function svgToDataUrl(svgString: string) {
	if (typeof window === 'undefined') {
		//Use node's Buffer
		const { Buffer } = require('buffer');
		return 'data:image/svg+xml;base64,' + Buffer.from(svgString).toString('base64');
	}

	//Use window btoa to convert to base64
	return 'data:image/svg+xml;base64,' + window.btoa(svgString);
}
